<template>
    <app-layout>
        <Head>
            <title>Warenkorb</title>
        </Head>

        <template v-slot:overline>
            <translate>jetzt Bestellen</translate>
        </template>

        <template v-slot:headline>
            <translate>Ihr Warenkorb</translate>
        </template>

        <template v-slot:stepper>
            <stepper>
                <step icon="list-basket-checkmark" class-names="active">
                    <translate>Prüfen</translate>
                </step>
                <step icon="delivery-shipment-packages-setting" class-names="active">
                    <translate>Adresse und Zahlungsart</translate>
                </step>
                <step icon="credit-card-security-protection">
                    <translate>Bezahlen</translate>
                </step>
            </stepper>
        </template>

        <div class="checkout">
            <form @submit.prevent="submit">
                <div class="row">
                    <div class="col col-8">
                        <card>
                            <card-header>
                                <card-title>
                                    <translate>Rechnungsadresse</translate>
                                </card-title>
                            </card-header>
                            <card-content>
                                <form-select id="client_address_id" :label="this.$gettext('Rechnungsadresse')"
                                             v-model="form.client_address_id" :options="clientAddressesOptions"
                                             v-if="clientAddressesOptions" @change="clientAddressesSelect"/>
                                <form-group>
                                    <form-input type="text" id="name" :label="this.$gettext('Name')" v-model="form.name"
                                                :error="form.errors.name" autocomplete="name"/>
                                    <form-input type="text" id="company" :label="this.$gettext('Firma')"
                                                v-model="form.company"
                                                :error="form.errors.company" autocomplete="organization"/>
                                </form-group>
                                <form-group>
                                    <form-input type="text" id="street" :label="this.$gettext('Straße')"
                                                v-model="form.street"
                                                :error="form.errors.street" class="three-four"
                                                autocomplete="street-address"/>
                                    <form-input type="text" id="street_no" :label="this.$gettext('Nr')"
                                                v-model="form.street_no"
                                                :error="form.errors.street_no" class="one-four"/>
                                </form-group>

                                <form-group>
                                    <form-input type="text" id="zip" :label="this.$gettext('Plz')" v-model="form.zip"
                                                :error="form.errors.zip"
                                                class="one-four" autocomplete="postal-code"/>
                                    <form-input type="text" id="city" :label="this.$gettext('Ort')" v-model="form.city"
                                                :error="form.errors.city" class="three-four"
                                                autocomplete="address-level2"/>
                                </form-group>

                                <form-select id="client_country_id" :label="this.$gettext('Land')"
                                             :options="clientCountries"
                                             v-model="form.client_country_id"
                                             :error="form.errors.client_country_id"/>

                                <form-checkbox id="store_client_address" v-model:checked="form.store_client_address"
                                               :error="form.errors.store_client_address"
                                               :option="this.$gettext('neue Rechnungsadresse im Adressbuch speichern')"
                                               v-if="newAddressButton"/>

                                <hr>
                                <form-input type="text" id="internal_order_number"
                                            :label="this.$gettext('Ihre Bestellnummer (Optional)')"
                                            v-model="form.internal_order_number"
                                            :error="form.errors.internal_order_number" autocomplete="off"/>
                            </card-content>
                        </card>
                    </div>
                    <div class="col col-4">
                        <div class="row">
                            <div class="col">
                                <card minify="true">
                                    <card-header>
                                        <card-overline>
                                            <translate>Kasse</translate>
                                        </card-overline>
                                    </card-header>
                                    <card-content>
                                        <div class="checkout--sidebar checkout--total">
                                            <translate>Summe Netto</translate>
                                            <span class="number">{{ this.$currency(total.net_price) }}</span>
                                            <translate>19% USt.</translate>
                                            <span class="number">{{ this.$currency(total.ust) }}</span>
                                            <strong>
                                                <translate>Gesamtsumme brutto</translate>
                                                <br>
                                                <small class="text-medium">
                                                    <translate>inkl. Versandkosten</translate>
                                                </small>
                                            </strong>
                                            <strong class="number">
                                                {{ this.$currency(total.offer_price) }}
                                            </strong>
                                        </div>
                                    </card-content>
                                </card>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <card minify="true">
                                    <card-header>
                                        <card-overline>
                                            <translate>Lieferadresse</translate>
                                        </card-overline>
                                    </card-header>
                                    <card-content>
                                        <p v-for="offer in offers" :key="offer">
                                            <strong>{{
                                                    offer.ask_for_quote.ask_for_quote_address?.company
                                                }}</strong><br>
                                            {{ offer.ask_for_quote.ask_for_quote_address?.name }}<br>
                                            {{ offer.ask_for_quote.ask_for_quote_address?.street }}
                                            {{ offer.ask_for_quote.ask_for_quote_address?.street_no }}<br>
                                            {{ offer.ask_for_quote.ask_for_quote_address?.zip }}
                                            {{ offer.ask_for_quote.ask_for_quote_address?.city }}
                                        </p>
                                    </card-content>
                                </card>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <card minify="true">
                                    <card-header>
                                        <card-overline>
                                            <translate>Zahlungsart</translate>
                                        </card-overline>
                                    </card-header>
                                    <card-content>
                                        <div class="checkout--sidebar">
                                            <div v-if="form.errors.payment_type"
                                                 class="form-element form-element--error">
                                                {{ form.errors.payment_type }}
                                            </div>
                                            <form-radio id="payment_type_iv" name="payment_type"
                                                        v-model:checked="form.payment_type" value="BL_IV"
                                                        :option="this.$gettext('Rechnung')"/>
                                            <form-radio :disabled="true" id="payment_type_cc" name="payment_type"
                                                        v-model:checked="form.payment_type" value="CC"
                                                        :option="this.$gettext('VISA/Mastercard')"/>
                                            <form-radio id="payment_type_va" name="payment_type"
                                                        v-model:checked="form.payment_type" value="VA"
                                                        :option="this.$gettext('PayPal Express')"/>
                                            <form-radio id="payment_type_so" name="payment_type"
                                                        v-model:checked="form.payment_type" value="OT_SO"
                                                        :option="this.$gettext('Sofortüberweisen (Deutschland)')"/>
                                            <form-radio id="payment_type_gp" name="payment_type"
                                                        v-model:checked="form.payment_type" value="OT_GP"
                                                        :option="this.$gettext('Giropay (Deutschland)')"/>

                                            <div v-show="form.payment_type === 'CC'">
                                                <form-group>
                                                    <form-input id="cc_owner_firstname" class="one-half"
                                                                :label="this.$gettext('Kreditkarteninhaber Vorname')"
                                                                v-model="form.cc_owner_firstname"/>
                                                    <form-input id="cc_owner_lastname" class="one-half"
                                                                :label="this.$gettext('Kreditkarteninhaber Nachname')"
                                                                v-model="form.cc_owner_lastname"/>
                                                </form-group>
                                                <form-input id="cc_owner_email"
                                                            :label="this.$gettext('Kreditkarten Inhaber Email')"
                                                            v-model="form.cc_owner_email"/>
                                                <div id="error-holder"></div>
                                                <div class="form-element form-element--unzer">
                                                    <div id="card-element-id-number" class="form-input">
                                                        <!-- Card number UI Element will be inserted here. -->
                                                    </div>
                                                </div>
                                                <form-group>
                                                    <div class="form-element form-element--unzer one-half">
                                                        <div id="card-element-id-expiry" class="form-input">
                                                            <!-- Card expiry date UI Element will be inserted here. -->
                                                        </div>
                                                    </div>
                                                    <div class="form-element form-element--unzer one-half">
                                                        <div id="card-element-id-cvc" class="form-input">
                                                            <!-- Card CVC UI Element will be inserted here. -->
                                                        </div>
                                                    </div>
                                                </form-group>
                                                <form-input type="hidden" id="cc_resource_id"
                                                            v-model="form.cc_resource_id"/>
                                                <p class="body-2">
                                                    <translate>Sie werden auf zur Authorisierung der Zahlung an die
                                                        Seite des Paymentanbieters weitergeleitet, Ihre Kreditkarte wird
                                                        noch nicht belastet.
                                                    </translate>
                                                </p>
                                            </div>

                                            <div v-show="form.payment_type === 'VA'">
                                                <form-input id="pp_owner_email" :label="this.$gettext('Paypal Account')"
                                                            placeholder="einkauf@domain.de"
                                                            v-model="form.pp_owner_email"/>
                                            </div>

                                            <form-actions>
                                                <form-link-button :href="checkoutCart" icon="button-arrow-left-circle"
                                                                  icon-before="true">
                                                    <translate>zurück</translate>
                                                </form-link-button>
                                                <form-button color="secondary" icon="button-arrow-right-circle" :disabled="form.processing" :class="{'btn--processing': form.processing}">
                                                    <translate>weiter</translate>
                                                </form-button>
                                            </form-actions>
                                        </div>
                                    </card-content>
                                </card>
                            </div>
                        </div>

                    </div>

                </div>
            </form>
        </div>

    </app-layout>
</template>

<script>
import AppLayout from '@partfactory/Layouts/AppLayout';
import Stepper from '@partfactory/Shared/Stepper/Stepper';
import Step from '@partfactory/Shared/Stepper/Step';
import Card from '@partfactory/Shared/Card/Card';
import CardContent from '@partfactory/Shared/Card/CardContent';
import CardOverline from '@partfactory/Shared/Card/CardOverline';
import CardHeader from '@partfactory/Shared/Card/CardHeader';
import FormButton from '@partfactory/Shared/Form/FormButton';
import FormActions from '@partfactory/Shared/Form/FormActions';
import FormLinkButton from '@partfactory/Shared/Form/FormLinkButton';
import CardTitle from '@partfactory/Shared/Card/CardTitle';
import FormGroup from '@partfactory/Shared/Form/FormGroup';
import {useForm} from '@inertiajs/vue3';
import FormInput from '@partfactory/Shared/Form/FormInput';
import FormSelect from '@partfactory/Shared/Form/FormSelect';
import FormRadio from '@partfactory/Shared/Form/FormRadio';
import FormCheckbox from '@partfactory/Shared/Form/FormCheckbox';

export default {
    name: 'Delivery',
    components: {
        FormCheckbox,
        FormRadio,
        FormSelect,
        FormInput,
        FormGroup,
        CardTitle,
        FormLinkButton,
        FormActions, FormButton, CardHeader, CardOverline, CardContent, Card, Step, Stepper, AppLayout
    },
    props: {
        cart: Object,
        clientAddresses: Object,
        askForQuoteAddress: Object,
        clientCountries: Object,
        payment_type: Object,
        pp_owner_email: Object,
    },
    data() {
        const clientAddress = this.clientAddresses.filter(clientAddress => {
            return clientAddress.default_address === true;
        });

        const form = useForm({
            client_address_id: this?.askForQuoteAddress ? null : clientAddress[0]?.id,
            name: this?.askForQuoteAddress?.name ? this?.askForQuoteAddress?.name : clientAddress[0]?.name ? clientAddress[0]?.name : this.$page.props.user.name,
            company: this?.askForQuoteAddress?.company ? this?.askForQuoteAddress?.company : clientAddress[0]?.company ? clientAddress[0]?.company : this.$page.props.user.company,
            street: this?.askForQuoteAddress?.street ? this?.askForQuoteAddress?.street : clientAddress[0]?.street,
            street_no: this?.askForQuoteAddress?.street_no ? this?.askForQuoteAddress?.street_no : clientAddress[0]?.street_no,
            zip: this?.askForQuoteAddress?.zip ? this?.askForQuoteAddress?.zip : clientAddress[0]?.zip,
            city: this?.askForQuoteAddress?.city ? this?.askForQuoteAddress?.city : clientAddress[0]?.city,
            client_country_id: this?.askForQuoteAddress?.client_country_id ? this?.askForQuoteAddress?.client_country_id : clientAddress[0]?.client_country_id,
            internal_order_number: this?.cart.length > 0 ? this?.cart[0]['ask_for_quote']['internal_order_number'] : '',
            store_client_address: this?.askForQuoteAddress?.store_client_address,
            payment_type: this?.payment_type,
            pp_owner_email: this?.pp_owner_email,
            cc_owner_firstname: null,
            cc_owner_lastname: null,
            cc_owner_email: null,
            cc_resource_id: null,

        });
        return {form};
    },
    setup() {

        // Create an Unzer instance with your public key
        // eslint-disable-next-line no-undef
        // let unzerInstance = new unzer('s-pub-2a10xHWFxlfPLaECzvQE4yQ7Kuv2gtcv');
        // // Todo: Config
        //
        // // Create a Card instance and render the input fields
        // let Card = unzerInstance.Card();
        // Card.create('number', {
        //     containerId: 'card-element-id-number',
        //     onlyIframe: false
        // });
        // Card.create('expiry', {
        //     containerId: 'card-element-id-expiry',
        //     onlyIframe: false
        // });
        // Card.create('cvc', {
        //     containerId: 'card-element-id-cvc',
        //     onlyIframe: false
        // });
        // return {Card};
    },
    computed: {
        checkoutCart() {
            return this.$$route('checkout.cart');
        },
        total() {
            let total = {
                net_price: 0,
                ust: 0,
                offer_price: 0,
            };
            if (this.cart) {
                this.cart.forEach(clientOrder => {
                    total.net_price += Number(clientOrder.net_price);
                    total.ust += Number(clientOrder.ust);
                    total.offer_price += Number(clientOrder.offer_price);
                });
            }
            return total;
        },
        offers() {
            let offer = Array;
            return Object.keys(this.cart).map(key => {

                offer[key] = this.cart[key];

                offer[key].ust = offer[key].offer_price - offer[key].net_price;
                offer[key].ust = (Math.round(offer[key].ust * 100) / 100).toFixed(2);

                return offer[key];
            });
        },
        clientAddressesOptions() {
            if (this.clientAddresses.length > 0) {
                return this.clientAddresses?.map(clientAddress => {
                    return {
                        value: clientAddress.id,
                        text: clientAddress.company + ', ' + clientAddress.street + ' ' + clientAddress.street_no + ', ' + clientAddress.zip + ' ' + clientAddress.city
                    };
                });
            }
            return null;
        },
        newAddressButton() {
            return this.clientAddresses.length < 6;
        }
    },
    methods: {
        clientAddressesSelect() {
            const clientAddress = this.clientAddresses.filter(clientAddress => {
                return clientAddress.id === Number(this.form.client_address_id);
            });

            this.form.client_address_id = clientAddress[0].id;
            this.form.name = clientAddress[0].name;
            this.form.company = clientAddress[0].company;
            this.form.street = clientAddress[0].street;
            this.form.street_no = clientAddress[0].street_no;
            this.form.zip = clientAddress[0].zip;
            this.form.city = clientAddress[0].city;
            this.form.client_country_id = clientAddress[0].client_country_id;
            this.form.phone = clientAddress[0].phone;
        },
        submit() {
            if (this.form.payment_type === 'CC') {
                let formProxy = this.form;
                const routeProxy = this.$$route('checkout.delivery.post');
                const tokenProxy = this.$page.props.csrf_token;
                this.Card.createResource()
                    .then(function (result) {
                        formProxy.cc_resource_id = result.id;

                        formProxy
                            .transform(data => ({
                                ...data,
                                store_client_address: this.form.store_client_address ? 'true' : '',
                                _token: tokenProxy,
                            }))
                            .post(routeProxy, {});
                    })
                    .catch(function (error) {
                        console.log(error);
                    });


            } else {
                this.form
                    .transform(data => ({
                        ...data,
                        store_client_address: this.form.store_client_address ? 'true' : '',
                        _token: this.$page.props.csrf_token,
                    }))
                    .post(this.$$route('checkout.delivery.post'), {});
            }

        },
    },
};
</script>
